/**
 * Every data key used by the widgets, so a key never has two fetchers.
 * Keys are prefixed by area (`stats`, `wo:`, `cal:`, `bookings:` ...) so after-action
 * invalidation can target a whole area with one prefix.
 */
import axios from 'axios';

const get = (url, params) => async ({ signal }) => (await axios.get(url, { params, signal })).data;
const listData = (response) => (Array.isArray(response) ? response : response?.results || []);
const getList = (url, params) => async ({ signal }) => listData((await axios.get(url, { params, signal })).data);
const getCount = (url, params) => async ({ signal }) => {
  const data = (await axios.get(url, { params: { ...params, page_size: 1 }, signal })).data;
  return Array.isArray(data) ? data.length : data?.count ?? 0;
};

export const dataSources = {
  stats: () => ({ key: 'stats', interval: 'normal', fetch: get('/api/dashboard/statistics/') }),

  maintenances: (params, name) => ({
    key: `wo:${name}:${JSON.stringify(params)}`,
    interval: 'normal',
    fetch: getList('/api/maintenances/', params),
  }),

  calendarDay: (date, view = 'employee') => ({
    key: `cal:day:${view}:${date}`,
    interval: 'live',
    fetch: get('/api/scheduling/calendar/', { start: date, end: date, view }),
  }),
  calendarRange: (start, end) => ({
    key: `cal:range:${start}:${end}`,
    interval: 'slow',
    fetch: get('/api/scheduling/calendar/', { start, end, view: 'employee' }),
  }),
  unscheduled: (limit = 10) => ({
    key: 'cal:unscheduled',
    interval: 'normal',
    fetch: get('/api/scheduling/calendar/unscheduled/', { limit }),
  }),

  approvals: (limit = 25, days = 7) => ({ key: `dash:approvals:${days}`, interval: 'normal', fetch: get('/api/dashboard/approvals/', { limit, days }) }),
  quotes: (limit = 25) => ({ key: 'dash:quotes', interval: 'normal', fetch: get('/api/dashboard/quotes/', { limit }) }),
  capacity: (date) => ({ key: `dash:capacity:${date}`, interval: 'normal', fetch: get('/api/dashboard/capacity/', { date }) }),

  timeclockBoard: () => ({ key: 'tc:board', interval: 'live', fetch: get('/api/scheduling/timeclock/board/') }),
  timeclockPendingAll: () => ({ key: 'tc:pending:all', interval: 'normal', fetch: get('/api/scheduling/timeclock/pending/', { scope: 'all' }) }),
  timeclockPendingSelf: () => ({ key: 'tc:pending:self', interval: 'normal', fetch: get('/api/scheduling/timeclock/pending/') }),
  unloggedDays: () => ({ key: 'tc:unlogged', interval: 'slow', fetch: get('/api/scheduling/work-days/unlogged_days/') }),
  employees: () => ({ key: 'emp:active', interval: 'slow', fetch: getList('/api/employees/', { state: 'active' }) }),

  invoiceable: () => ({ key: 'inv:invoiceable', interval: 'normal', fetch: get('/api/invoices/invoiceable-maintenances/') }),
  invoices: (params, name) => ({ key: `inv:${name}`, interval: 'normal', fetch: getList('/api/invoices/', params) }),

  cashRegister: (period = 'day') => ({ key: `rep:cash:${period}`, interval: 'slow', fetch: get('/api/reports/cash-register/', { period }) }),
  summary: (period = 'month') => ({ key: `rep:summary:${period}`, interval: 'slow', fetch: get('/api/reports/summary/', { period }) }),
  customersReport: (period = 'month') => ({ key: `rep:customers:${period}`, interval: 'slow', fetch: get('/api/reports/customers/', { period }) }),
  performance: (period = 'month') => ({ key: `rep:perf:${period}`, interval: 'slow', fetch: get('/api/reports/employees/performance/', { period }) }),
  worktime: (period = 'week') => ({ key: `rep:worktime:${period}`, interval: 'slow', fetch: get('/api/reports/employees/worktime/', { period }) }),
  profitability: (period = 'month') => ({ key: `rep:margin:${period}`, interval: 'slow', fetch: get('/api/reports/profitability/work-orders/', { period }) }),
  workOrdersReport: (period = 'month') => ({ key: `rep:wo:${period}`, interval: 'slow', fetch: get('/api/reports/work-orders/', { period }) }),

  bookingsCounts: () => ({ key: 'bookings:counts', interval: 'normal', fetch: get('/api/bookings/dashboard_counts/') }),
  bookingsUpcoming: (today) => ({
    key: `bookings:upcoming:${today}`,
    interval: 'normal',
    fetch: getList('/api/bookings/', {
      date_from: today,
      exclude_status: 'cancelled_by_customer,cancelled_by_workshop,no_show',
      page_size: 25,
    }),
  }),
  bookingsPending: () => ({ key: 'bookings:pending', interval: 'normal', fetch: getList('/api/bookings/', { status: 'pending', page_size: 25 }) }),

  partsSales: (params, name) => ({ key: `ps:${name}`, interval: 'normal', fetch: get('/api/parts-sales/', params) }),

  pendingParts: () => ({ key: 'sup:pending', interval: 'normal', fetch: get('/api/supplier-orders/pending-parts/') }),
  supplierOrderCount: (status) => ({ key: `sup:orders:${status}`, interval: 'slow', fetch: getCount('/api/supplier-orders/', { status }) }),
  inventorySummary: () => ({ key: 'stock:summary', interval: 'slow', fetch: get('/api/inventory/summary/') }),
  lowStock: () => ({ key: 'stock:low', interval: 'slow', fetch: getList('/api/inventory/', { low_stock: 'true', page_size: 10 }) }),
  inboundCounts: () => ({ key: 'inbound:counts', interval: 'normal', fetch: get('/api/inbound-delivery-notes/counts/') }),
  purchaseLedgerSummary: () => ({ key: 'purchase:summary', interval: 'normal', fetch: get('/api/purchase-ledger/invoices/summary/') }),
  partSuggestions: () => ({
    key: 'parts:suggestions',
    interval: 'slow',
    fetch: async ({ signal }) => {
      const [email, extension] = await Promise.all([
        axios.get('/api/parts/email-suggestions/', { signal }),
        axios.get('/api/parts/extension-suggestions/', { signal }),
      ]);
      const count = (payload) => {
        const rows = listData(payload);
        return rows.reduce((sum, row) => sum + (Number(row.pending_count) || 0), Number(payload?.pending_count) || 0);
      };
      return { pending: count(email.data) + count(extension.data) };
    },
  }),
  priceDrift: () => ({ key: 'parts:drift', interval: 'slow', fetch: get('/api/parts/price-drift/', { count_only: 'true' }) }),

  feedbackStats: () => ({ key: 'fb:stats', interval: 'slow', fetch: get('/api/feedback-stats/') }),
  feedbackCallbacks: () => ({ key: 'fb:callbacks', interval: 'normal', fetch: getList('/api/feedback-responses/', { followup_unactioned: 'true', page_size: 10 }) }),
  remindersUpcoming: () => ({ key: 'rem:upcoming', interval: 'slow', fetch: get('/api/reminder-rules/upcoming/') }),
  remindersStats: () => ({ key: 'rem:stats', interval: 'slow', fetch: get('/api/reminder-sends/stats/') }),
  aiPhoneNew: () => ({ key: 'ai:new', interval: 'normal', fetch: getList('/api/ai-phone-calls/', { status: 'new', page_size: 10 }) }),
  tireCapacity: () => ({ key: 'th:capacity', interval: 'slow', fetch: get('/api/tire-hotel-cards/capacity/') }),
  tireFlagged: () => ({ key: 'th:flagged', interval: 'slow', fetch: getList('/api/tire-hotel-cards/flagged/') }),
  whatsNew: () => ({ key: 'wn:list', interval: 'slow', fetch: get('/api/users/whatsnew/') }),
  invitations: () => ({ key: 'org:invites', interval: 'slow', fetch: getList('/api/organization-invitations/') }),
  subscriptionInfo: () => ({ key: 'pay:info', interval: 'slow', fetch: get('/api/payments/subscription/info/') }),
  seats: () => ({ key: 'pay:seats', interval: 'slow', fetch: get('/api/payments/seats/') }),
  usage: () => ({ key: 'pay:usage', interval: 'slow', fetch: get('/api/payments/usage/') }),
  lookupQuota: () => ({ key: 'veh:quota', interval: 'slow', fetch: get('/api/vehicles/lookup-quota/') }),
};

/** Convenience: `useWidgetData` arguments from a data source. */
export function sourceArgs(source) {
  return [source.key, source.fetch, { interval: source.interval }];
}
